<template>
  <div class="status-screen">
    <div class="status-screen__badge" :class="'status-screen__badge--' + tone">
      <slot name="icon" />
    </div>

    <h1 class="status-screen__title">{{ title }}</h1>
    <p v-if="message" class="status-screen__message">{{ message }}</p>

    <div v-if="$slots.body" class="status-screen__body">
      <slot name="body" />
    </div>

    <div v-if="$slots.actions" class="status-screen__actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<script>
// Shared layout for system/empty/result screens (errors, 404, payment issues):
// a soft tinted icon badge, a title, an optional message, plus body/actions slots.
// `tone` picks the badge palette from the design tokens.
export default {
  name: "VStatusScreen",
  props: {
    title: { type: String, required: true },
    message: { type: String, default: "" },
    tone: {
      type: String,
      default: "brand",
      validator: (v) => ["brand", "danger", "warning", "success", "info"].includes(v),
    },
  },
};
</script>

<style lang="scss" scoped>
.status-screen {
  /* viewport units, not %: the document scrolls, ancestor heights are content-driven */
  min-height: 100vh;
  min-height: 100svh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 18px;

  padding: 32px 24px calc(32px + var(--safe-bottom, 0px));
}

.status-screen__badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 92px;
  height: 92px;
  border-radius: 50%;

  animation: status-pop 0.42s var(--ease-spring) both;

  ::v-deep svg {
    width: 44px;
    height: 44px;
  }
}

@keyframes status-pop {
  from { transform: scale(0.5); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
}

.status-screen__badge--brand   { background: var(--color-primary-soft); color: var(--color-primary); }
.status-screen__badge--info    { background: var(--info-soft); color: var(--color-primary); }
.status-screen__badge--danger  { background: var(--danger-soft); color: var(--danger); }
.status-screen__badge--warning { background: var(--warning-soft); color: var(--warning); }
.status-screen__badge--success { background: var(--success-soft); color: var(--success); }

.status-screen__title {
  margin: 0;
  max-width: 320px;
  font-size: var(--text-2xl);
  font-weight: var(--weight-black);
  letter-spacing: -0.01em;
  line-height: 1.15;
  color: var(--text-primary);
}

.status-screen__message {
  margin: 0;
  max-width: 320px;
  font-size: var(--text-base);
  line-height: 1.5;
  color: var(--text-secondary);
}

.status-screen__body {
  width: 100%;
  max-width: 360px;
  margin-top: 2px;
}

.status-screen__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 320px;
  margin-top: 6px;
}
</style>
